import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft } from 'lucide-react';
import Footer from './Footer';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-black text-white py-6">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-500 mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
          <h1 className="text-4xl font-bold">Terms of Service</h1>
          <p className="text-gray-400 mt-2">Last Updated: January 2025</p>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-4xl">
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to eBlackBookListings. By accessing or using our website (www.eBlackBookListings.com), purchasing our products, or using our services, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our website or services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Description of Services</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              eBlackBookListings provides the following services:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li><strong>Digital Products:</strong> Sale and delivery of downloadable PDF guides, resources, and educational materials for authors ("Authors Social Media eCoSystem" and related products)</li>
              <li><strong>Ghostwriting Services:</strong> Professional ghostwriting and manuscript development services</li>
              <li><strong>Marketing Services:</strong> Marketing, design, and promotional services for authors and creators</li>
              <li><strong>Business Directory:</strong> eBlackBookListings directory for business listings</li>
              <li><strong>Physical Books:</strong> Sale of physical books (coming soon)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Digital Product Purchases</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.1 Payment and Pricing</h3>
            <p className="text-gray-700 leading-relaxed">
              All prices are listed in US Dollars (USD). Payment is processed securely through Stripe. By making a purchase, you agree to pay the stated price plus any applicable taxes. Prices are subject to change without notice.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.2 Digital Product Delivery</h3>
            <p className="text-gray-700 leading-relaxed">
              Upon successful payment, you will receive immediate access to download your purchased digital products. Download links are provided on the order confirmation page and sent to your email address. Download links are valid for a limited time and have download limits to prevent unauthorized sharing.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.3 License Grant</h3>
            <p className="text-gray-700 leading-relaxed mb-3">
              When you purchase a digital product, you are granted a personal, non-exclusive, non-transferable license to use the product for your personal use only. You may not:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Resell, redistribute, or share the digital products</li>
              <li>Modify, adapt, or create derivative works</li>
              <li>Use the products for commercial purposes without written permission</li>
              <li>Remove or alter any copyright, trademark, or proprietary notices</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">3.4 Refund Policy</h3>
            <p className="text-gray-700 leading-relaxed">
              Due to the immediate delivery and nature of digital products, all sales are final. We do not offer refunds once the download link has been accessed. However, if you experience technical issues with downloading or accessing your purchase, please contact us at hello@audreyyoung.com within 7 days of purchase, and we will work to resolve the issue.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Service-Based Offerings</h2>
            
            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.1 Ghostwriting Services</h3>
            <p className="text-gray-700 leading-relaxed">
              Ghostwriting services are provided on a custom, project-by-project basis. Specific terms, deliverables, timelines, and pricing will be outlined in a separate service agreement. A deposit may be required before work begins.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Marketing Services</h3>
            <p className="text-gray-700 leading-relaxed">
              Marketing and design services are provided based on individual client needs. Service details, scope, and pricing will be defined in a separate agreement.
            </p>

            <h3 className="text-xl font-semibold text-gray-800 mb-3 mt-6">4.3 eBlackBookListings Directory</h3>
            <p className="text-gray-700 leading-relaxed">
              Submission to the eBlackBookListings business directory is subject to review and approval. We reserve the right to reject or remove any listing at our discretion.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. User Accounts and Security</h2>
            <p className="text-gray-700 leading-relaxed">
              You are responsible for maintaining the confidentiality of any account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized access or use of your account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Intellectual Property Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on this website, including but not limited to text, graphics, logos, images, digital products, and software, is the property of eBlackBookListings or its content suppliers and is protected by United States and international copyright laws. Unauthorized use of any materials on this site may violate copyright, trademark, and other laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. User-Generated Content</h2>
            <p className="text-gray-700 leading-relaxed">
              If you submit any content to our website (reviews, comments, testimonials, etc.), you grant us a non-exclusive, royalty-free, perpetual, worldwide license to use, reproduce, modify, and display such content in connection with our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Prohibited Uses</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              You agree not to use our website or services to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon the rights of others</li>
              <li>Transmit any harmful or malicious code</li>
              <li>Engage in any form of harassment or abuse</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the proper functioning of the website</li>
              <li>Scrape or harvest data from the website</li>
              <li>Impersonate any person or entity</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              OUR WEBSITE AND SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT OUR WEBSITE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE MAKE NO WARRANTIES REGARDING THE ACCURACY, RELIABILITY, OR COMPLETENESS OF ANY CONTENT.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, eBlackBookListings SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM YOUR USE OF OUR SERVICES.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless eBlackBookListings, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with your use of our services or violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Third-Party Services</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website uses third-party services, including Stripe for payment processing. Your use of these services is subject to their respective terms and conditions. We are not responsible for any issues arising from your use of third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Governing Law and Dispute Resolution</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of our services shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Modifications to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services after any changes constitutes acceptance of the revised Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Termination</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to suspend or terminate your access to our website and services at any time, without notice, for any reason, including violation of these Terms.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-gray-700"><strong>Email:</strong> info@eBlackBookListings.com</p>
              <p className="text-gray-700 mt-2"><strong>Website:</strong> www.eBlackBookListings.com</p>
            </div>
          </section>

          <section className="mb-8">
            <p className="text-gray-700 leading-relaxed italic">
              By using eBlackBookListings, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsOfService;